import { useEffect, useRef, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, MenuItem, Rating, Select,
  Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, alpha,
} from '@mui/material'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import CloseIcon from '@mui/icons-material/Close'
import MicOutlinedIcon from '@mui/icons-material/MicOutlined'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined'
import PauseOutlinedIcon from '@mui/icons-material/PauseOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import { Link as RouterLink } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { WorkOrder } from '@cmms/types'

import { api } from '../../../lib/api'
import { toast } from '../../../store/snackbarStore'
import { DocumentViewer } from '../../../components/DocumentViewer'
import { evaluateCondition } from '../../checklists/evaluateCondition'
import { ImageAnnotator } from '../../../components/ImageAnnotator'

// Fixed labels + traffic-light coloring for the 'condition' item type
// (Good/Medium/Poor) — matches ChecklistBuilder.tsx's own copy of
// these constants (small enough that duplicating rather than sharing
// across the two features matches how 'yes'/'no'/'pass'/'fail' are
// already hardcoded independently in each file rather than imported).
const CONDITION_OPTIONS = ['Good', 'Medium', 'Poor']
const CONDITION_COLORS: Record<string, string> = { Good: '#059669', Medium: '#D97706', Poor: '#DC2626' }

// 'working' item type — stored values stay the single-token
// 'working'/'not_working' convention (matching yes_no/pass_fail), this
// is purely the button display label.
const WORKING_OPTIONS: { value: string; label: string; color?: string }[] = [
  { value: 'working', label: 'Working', color: '#059669' },
  { value: 'not_working', label: 'Not Working', color: '#DC2626' },
  { value: 'na', label: 'N/A' },
]

/// Normalised media-array reader — the column is JSONB so the API
/// sometimes hands us a parsed array, sometimes a JSON string, and
/// rows with nothing attached come back as `null`. All three are
/// handled here so callers never have to think about it.
function parseMedia(raw: unknown): Array<{ url: string; name: string; size: number; type: string }> {
  if (!raw) return []
  if (Array.isArray(raw)) return raw as any
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) } catch { return [] }
  }
  return []
}

interface EvidenceStatus {
  requireComment: boolean; hasComment: boolean
  requireImage:   boolean; hasImage:   boolean
  requireVoice:   boolean; hasVoice:   boolean
  /** True iff every required-evidence flag has been satisfied (or
   *  none are set). The Submit gate uses this on every visible item. */
  allMet: boolean
}

/// Decides whether a response-item row carries a real answer.
/// Mirrors the server-side `_isResponseItemAnswered` — a row may
/// exist with only a note attached (the UI upserts the row on every
/// notes keystroke), so existence alone isn't enough; we have to
/// look at the actual value_* columns.
function isAnsweredItem(answer: any): boolean {
  if (!answer) return false
  if (typeof answer.value_text === 'string' && answer.value_text.trim().length > 0) return true
  if (answer.value_number !== null && answer.value_number !== undefined) return true
  const opts = answer.value_options
  if (Array.isArray(opts) && opts.length > 0) return true
  if (typeof opts === 'string' && opts.length > 0) {
    try {
      const parsed = JSON.parse(opts)
      if (Array.isArray(parsed) && parsed.length > 0) return true
    } catch { /* malformed — treat as unanswered */ }
  }
  return false
}

/// Per-question status for the three evidence gates. Mirrors the
/// server-side check in `submitResponse` — we keep them aligned so
/// the client gates the button and the server stays the source of
/// truth in case of a mis-built client.
function evidenceStatus(item: any, answer: any): EvidenceStatus {
  const media = parseMedia(answer?.media_urls)
  const requireComment = !!item.require_comment
  const requireImage   = !!item.require_image
  const requireVoice   = !!item.require_voice
  const hasComment = typeof answer?.notes === 'string' && answer.notes.trim().length > 0
  const hasImage   = media.some((m) => typeof m?.type === 'string' && m.type.startsWith('image/'))
  const hasVoice   = media.some((m) => typeof m?.type === 'string' && m.type.startsWith('audio/'))
  const allMet =
    (!requireComment || hasComment) &&
    (!requireImage   || hasImage)   &&
    (!requireVoice   || hasVoice)
  return { requireComment, hasComment, requireImage, hasImage, requireVoice, hasVoice, allMet }
}

interface Props { workOrder: WorkOrder }

/**
 * Wrapper that surfaces every checklist attached to a WO — the legacy
 * primary `checklist_id` plus anything in the multi-link
 * `work_order_checklists` table. Each template renders as its own
 * executable card via `_ChecklistItem`. Order: primary first, then
 * linked in the order the API returns them.
 */
export function ChecklistExecution({ workOrder }: Props) {
  const client = workOrder.kind === 'task' ? api.tasks : api.workOrders
  const linkedQ = useQuery({
    queryKey: ['wo-linked-checklists', workOrder.id],
    queryFn: () => client.listLinkedChecklists(workOrder.id),
    enabled: true,
  })
  const linked = Array.isArray(linkedQ.data) ? linkedQ.data : []

  // Build the de-duped, ordered list: primary first, then any linked
  // templates not already in the primary slot.
  const templateIds: string[] = []
  if (workOrder.checklist_id) templateIds.push(workOrder.checklist_id)
  for (const l of linked) {
    if (!templateIds.includes(l.template_id)) templateIds.push(l.template_id)
  }

  if (templateIds.length === 0) return null

  return (
    <Stack spacing={3} sx={{ mb: 4 }}>
      {templateIds.map((tid, i) => (
        <_ChecklistItem
          key={tid}
          workOrder={workOrder}
          templateId={tid}
          isPrimary={i === 0}
          totalCount={templateIds.length}
          ordinal={i + 1}
        />
      ))}
    </Stack>
  )
}

interface ItemProps {
  workOrder: WorkOrder
  templateId: string
  isPrimary: boolean
  totalCount: number
  ordinal: number
}

function _ChecklistItem({ workOrder, templateId, totalCount, ordinal }: ItemProps) {
  const qc = useQueryClient()

  const responsesQ = useQuery({
    queryKey: ['wo-checklist-responses', workOrder.id],
    queryFn: () => api.checklists.listResponses(workOrder.id),
  })
  const allResponses = Array.isArray(responsesQ.data) ? responsesQ.data : []
  // Multi-checklist: filter responses to *this* template. Without this
  // every item card would show the same response, racing each other on
  // submit.
  const responses = allResponses.filter((r) => r.template_id === templateId)
  const activeResponse = responses[0]

  const [responseDetail, setResponseDetail] = useState<any>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)

  useEffect(() => {
    if (activeResponse?.id) {
      setLoadingDetail(true)
      api.checklists.getResponse(workOrder.id, activeResponse.id)
        .then(setResponseDetail)
        .catch(() => {})
        .finally(() => setLoadingDetail(false))
    }
  }, [activeResponse?.id])

  const startM = useMutation({
    mutationFn: () => api.checklists.createResponse(workOrder.id, { template_id: templateId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wo-checklist-responses', workOrder.id] }); toast.success('Checklist started') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const submitM = useMutation({
    mutationFn: () => api.checklists.submitResponse(workOrder.id, activeResponse!.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wo-checklist-responses', workOrder.id] }); toast.success('Checklist submitted!') },
    onError: (e) => {
      // The server enforces the same evidence gate the client does;
      // when it bounces a submit it ships an `unmet` array. Surface
      // a per-question summary so the tech knows exactly what's
      // missing instead of just "Failed".
      const unmet = (e as any)?.payload?.unmet as Array<{ label: string; missing: string }> | undefined
      if (Array.isArray(unmet) && unmet.length > 0) {
        const preview = unmet.slice(0, 3).map((u) => `${u.label} (${u.missing})`).join('; ')
        toast.error(
          `Add the required ${unmet.length === 1 ? 'evidence' : 'evidence'} before submitting: ${preview}${unmet.length > 3 ? '…' : ''}`,
        )
        return
      }
      toast.error(e instanceof ApiError ? e.message : 'Failed')
    },
  })

  const woNotStarted = workOrder.status === 'open'
  const isTask = workOrder.kind === 'task'
  const woNoun = isTask ? 'task' : 'work order'

  // Header label — when there are multiple checklists, show "1 of 3" so
  // the user can tell them apart at a glance.
  const headerLabel = totalCount > 1
    ? `Checklist ${ordinal} of ${totalCount}`
    : 'Checklist required'

  // Not started — show the "Start checklist" CTA. Gated only by the
  // WO being in progress (no per-checklist execution window anymore).
  if (responses.length === 0) {
    const headerCopy = woNotStarted
      ? `Start the ${woNoun} first`
      : 'A checklist must be completed before closing'
    const bodyCopy = woNotStarted
      ? `The checklist will be available once the ${woNoun} is in progress`
      : 'Start the checklist to fill out all required items'

    return (
      <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1.5 }}>
          {headerLabel}
        </Typography>
        <Box sx={{ p: 3, borderRadius: 2, border: '2px dashed',
          borderColor: woNotStarted ? 'divider' : 'warning.main',
          bgcolor: (t) => alpha(
            woNotStarted ? t.palette.text.disabled : t.palette.warning.main,
            0.04
          ), textAlign: 'center' }}>
          <ChecklistOutlinedIcon sx={{
            fontSize: 28,
            color: woNotStarted ? 'text.disabled' : 'warning.main',
            mb: 1,
          }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>{headerCopy}</Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>{bodyCopy}</Typography>
          {!woNotStarted && (
            <Button variant="contained" size="small" startIcon={<ChecklistOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => startM.mutate()} disabled={startM.isPending}
              sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2 }}>
              {startM.isPending ? 'Starting...' : 'Start checklist'}
            </Button>
          )}
        </Box>
      </Box>
    )
  }

  if (loadingDetail || !responseDetail?.template_snapshot) {
    return <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
  }

  const snapshot = responseDetail.template_snapshot
  const answeredMap: Record<string, any> = {}
  for (const item of (responseDetail.response_items ?? [])) answeredMap[item.item_id] = item

  const isSubmitted = activeResponse?.status === 'submitted'
  const answeredCount = Object.keys(answeredMap).length
  const totalItems = (snapshot.sections ?? []).reduce((s: number, sec: any) =>
    s + (sec.items ?? []).filter((i: any) => i.type !== 'instruction' && evaluateCondition(i.condition, answeredMap)).length, 0)

  // Count visible questions that are blocking submission. Two
  // independent gates:
  //   - Required-but-unanswered (item.required && no value_*)
  //   - Required-evidence missing (require_comment/image/voice flags)
  // We tally them separately so the warning line can call out the
  // bigger problem (no answer at all) without burying it under a
  // longer "and also missing a photo" tail.
  const visibleItems = (snapshot.sections ?? [])
    .flatMap((sec: any) => sec.items ?? [])
    .filter((i: any) => i.type !== 'instruction' && evaluateCondition(i.condition, answeredMap))
  const requiredMissingCount = visibleItems
    .filter((i: any) => i.required && !isAnsweredItem(answeredMap[i.id])).length
  const evidenceMissingCount = visibleItems
    .filter((i: any) =>
      (i.require_comment || i.require_image || i.require_voice) &&
      !evidenceStatus(i, answeredMap[i.id]).allMet,
    ).length
  const unmetCount = requiredMissingCount + evidenceMissingCount

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
          {totalCount > 1 ? `${ordinal} of ${totalCount} — ` : ''}{snapshot.name}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{answeredCount}/{totalItems}</Typography>
          <Chip label={isSubmitted ? 'Submitted' : 'In progress'} size="small"
            sx={{ height: 20, fontSize: 10, fontWeight: 700,
              bgcolor: isSubmitted ? '#05966918' : '#D9770618',
              color:   isSubmitted ? '#059669'   : '#D97706' }} />
        </Stack>
      </Stack>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        {(snapshot.sections ?? []).map((section: any) => (
          <Box key={section.id}>
            <Box sx={{ px: 2.5, py: 1.5, bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)',
              borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{section.title}</Typography>
            </Box>
            {(section.items ?? []).map((item: any) => {
              const visible = evaluateCondition(item.condition, answeredMap)
              if (!visible) return null
              return (
                <ChecklistItemRow key={item.id} item={item} workOrderId={workOrder.id} responseId={responseDetail.id}
                  currentAnswer={answeredMap[item.id]} readOnly={isSubmitted}
                  onAnswered={() => {
                    api.checklists.getResponse(workOrder.id, responseDetail.id).then(setResponseDetail).catch(() => {})
                  }} />
              )
            })}
          </Box>
        ))}
      </Box>

      {!isSubmitted && (
        <Stack spacing={0.75} sx={{ mt: 2 }}>
          {requiredMissingCount > 0 && (
            <Typography sx={{ fontSize: 12, color: 'error.main', fontWeight: 600 }}>
              {requiredMissingCount === 1
                ? '1 required question still needs an answer'
                : `${requiredMissingCount} required questions still need an answer`}
            </Typography>
          )}
          {evidenceMissingCount > 0 && (
            <Typography sx={{ fontSize: 12, color: 'warning.main', fontWeight: 600 }}>
              {evidenceMissingCount === 1
                ? '1 question is missing required evidence (comment / image / voice)'
                : `${evidenceMissingCount} questions are missing required evidence (comment / image / voice)`}
            </Typography>
          )}
          <Button variant="contained" fullWidth
            onClick={() => submitM.mutate()}
            disabled={submitM.isPending || unmetCount > 0}
            sx={{ mt: 0.5, fontSize: 13, fontWeight: 600, borderRadius: 2, py: 1 }}>
            {submitM.isPending ? 'Submitting...' : 'Submit checklist'}
          </Button>
        </Stack>
      )}

      {isSubmitted && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 2, p: 1.5, borderRadius: 1.5,
          bgcolor: (t) => alpha(t.palette.success.main, 0.06), border: '1px solid', borderColor: '#059669' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#059669' }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#059669' }}>
              Checklist completed — {answeredCount} items answered
            </Typography>
          </Stack>
          {/* Only shown when the template has scored questions — a
              template with no weighted questions leaves score null. */}
          {activeResponse?.score != null && (
            <Chip label={`Score: ${activeResponse.score}%`} size="small"
              sx={{ height: 22, fontSize: 11.5, fontWeight: 700, bgcolor: '#059669', color: '#fff' }} />
          )}
        </Stack>
      )}
    </Box>
  )
}

function ChecklistItemRow({ item, workOrderId, responseId, currentAnswer, readOnly, onAnswered }: {
  item: any; workOrderId: string; responseId: string; currentAnswer?: any; readOnly: boolean; onAnswered: () => void
}) {
  const [value, setValue] = useState(currentAnswer?.value_text ?? '')
  const [numValue, setNumValue] = useState(currentAnswer?.value_number != null ? String(currentAnswer.value_number) : '')
  const [notes, setNotes] = useState(currentAnswer?.notes ?? '')
  const [showNotes, setShowNotes] = useState(!!currentAnswer?.notes)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rawMedia = currentAnswer?.media_urls
  const mediaUrls: Array<{ url: string; name: string; size: number; type: string }> = (() => {
    if (!rawMedia) return []
    if (Array.isArray(rawMedia)) return rawMedia
    if (typeof rawMedia === 'string') { try { return JSON.parse(rawMedia) } catch { return [] } }
    return []
  })()
  const [uploading, setUploading] = useState(false)
  const [viewIdx, setViewIdx] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Holds the picked image while the markup dialog is open. Non-image
  // files (pdf, doc) skip the dialog entirely and upload as-is.
  const [annotatorFile, setAnnotatorFile] = useState<File | null>(null)

  // Only resync from server when the answer row's *identity* changes —
  // i.e. when the user navigates to a different question or the answer
  // gets created for the first time. The previous code watched the
  // whole `currentAnswer` object, which is a fresh reference after
  // every debounced save → response refetch → parent re-render. That
  // dropped keystrokes mid-typing because each save echoed back into
  // `setValue` and clobbered text the user had already typed past.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setValue(currentAnswer?.value_text ?? '')
    setNumValue(currentAnswer?.value_number != null ? String(currentAnswer.value_number) : '')
    setNotes(currentAnswer?.notes ?? '')
    setShowNotes(!!currentAnswer?.notes)
  }, [currentAnswer?.id])

  if (item.type === 'instruction') {
    return (
      <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider',
        bgcolor: (t) => alpha(t.palette.info.main, 0.03) }}>
        <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: 'text.secondary' }}>{item.label}</Typography>
      </Box>
    )
  }

  function save(data: any) {
    if (readOnly) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        await api.checklists.upsertResponseItem(workOrderId, responseId, item.id, { ...data, notes: notes || undefined })
        onAnswered()
      } catch {}
    }, 500)
  }

  function saveNotes(n: string) {
    setNotes(n)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        await api.checklists.upsertResponseItem(workOrderId, responseId, item.id, {
          value_text: value || undefined, value_number: numValue ? parseFloat(numValue) : undefined, notes: n || undefined,
        })
        onAnswered()
      } catch {}
    }, 800)
  }

  // Use the shared helper so the "Answered" badge follows the same
  // truth-table the Submit gate does — a row with only a typed note
  // shouldn't count as answered.
  const hasAnswer = isAnsweredItem(currentAnswer)

  const ev = evidenceStatus(item, currentAnswer)

  // Already-linked follow-up — shown as a chip so the tech can see the
  // cascade from a previous escalation without re-creating it.
  const linkedWoId    = currentAnswer?.linked_work_order_id ?? null
  const linkedWoCode  = currentAnswer?.linked_work_order_code ?? null
  const linkedWoTitle = currentAnswer?.linked_work_order_title ?? null
  const linkedReqId    = currentAnswer?.linked_request_id ?? null
  const linkedReqTitle = currentAnswer?.linked_request_title ?? null
  const [reportOpen, setReportOpen] = useState(false)

  return (
    <Box sx={{ px: 2.5, py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1.25, flex: 1 }}>
          {item.label}
          {item.required && <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Typography>}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {readOnly && hasAnswer && (
            <Chip label="Answered" size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#05966918', color: '#059669' }} />
          )}
          {!readOnly && (
            <Tooltip title="Report an issue from this item — creates a follow-up work order or request" arrow>
              <IconButton size="small" onClick={() => setReportOpen(true)}
                sx={{
                  width: 28, height: 28, color: '#D97706',
                  bgcolor: (t) => alpha('#D97706', 0.08),
                  '&:hover': { bgcolor: (t) => alpha('#D97706', 0.16) },
                }}>
                <ReportProblemOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {/* Existing follow-up link chip (if one was created from this item) */}
      {(linkedWoId || linkedReqId) && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
          {linkedWoId && (
            <Chip
              component={RouterLink as any}
              to={`/work-orders/${linkedWoId}`}
              clickable
              icon={<BuildOutlinedIcon sx={{ fontSize: 13 }} />}
              label={`${linkedWoCode ?? 'WO'} · ${linkedWoTitle ?? 'Work order'}`}
              size="small"
              deleteIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 12 }} />}
              onDelete={() => {}}
              sx={{
                height: 22, fontSize: 11, fontWeight: 600,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.10),
                color: 'primary.main',
                '& .MuiChip-icon': { color: 'primary.main' },
                '& .MuiChip-deleteIcon': { color: 'primary.main' },
              }} />
          )}
          {linkedReqId && (
            <Chip
              component={RouterLink as any}
              to={`/requests/${linkedReqId}`}
              clickable
              icon={<AssignmentOutlinedIcon sx={{ fontSize: 13 }} />}
              label={`Request · ${linkedReqTitle ?? 'Request'}`}
              size="small"
              deleteIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 12 }} />}
              onDelete={() => {}}
              sx={{
                height: 22, fontSize: 11, fontWeight: 600,
                bgcolor: (t) => alpha(t.palette.warning.main, 0.10),
                color: 'warning.dark',
                '& .MuiChip-icon': { color: 'warning.dark' },
                '& .MuiChip-deleteIcon': { color: 'warning.dark' },
              }} />
          )}
        </Stack>
      )}

      {reportOpen && (
        <_ReportFromItemDialog
          workOrderId={workOrderId} responseId={responseId} itemId={item.id}
          itemLabel={item.label}
          onClose={() => setReportOpen(false)}
          onCreated={() => { setReportOpen(false); onAnswered() }} />
      )}

      {/* Evidence-gate chips. Always visible when the question carries
          a require_* flag — green when the matching evidence is
          attached, amber when still missing. Helps the tech see at a
          glance what's blocking submission. */}
      {(ev.requireComment || ev.requireImage || ev.requireVoice) && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25, rowGap: 0.5 }}>
          {ev.requireComment && (
            <Chip
              icon={<ChatBubbleOutlineIcon sx={{ fontSize: 11 }} />}
              label={ev.hasComment ? 'Comment ✓' : 'Comment required'}
              size="small"
              sx={{
                height: 20, fontSize: 10, fontWeight: 700,
                bgcolor: (t) => ev.hasComment
                  ? alpha(t.palette.success.main, 0.12) : alpha(t.palette.warning.main, 0.12),
                color: ev.hasComment ? '#059669' : '#D97706',
              }}
            />
          )}
          {ev.requireImage && (
            <Chip
              icon={<AddPhotoAlternateOutlinedIcon sx={{ fontSize: 11 }} />}
              label={ev.hasImage ? 'Image ✓' : 'Image required'}
              size="small"
              sx={{
                height: 20, fontSize: 10, fontWeight: 700,
                bgcolor: (t) => ev.hasImage
                  ? alpha(t.palette.success.main, 0.12) : alpha(t.palette.warning.main, 0.12),
                color: ev.hasImage ? '#059669' : '#D97706',
              }}
            />
          )}
          {ev.requireVoice && (
            <Chip
              icon={<MicOutlinedIcon sx={{ fontSize: 11 }} />}
              label={ev.hasVoice ? 'Voice note ✓' : 'Voice note required'}
              size="small"
              sx={{
                height: 20, fontSize: 10, fontWeight: 700,
                bgcolor: (t) => ev.hasVoice
                  ? alpha(t.palette.success.main, 0.12) : alpha(t.palette.warning.main, 0.12),
                color: ev.hasVoice ? '#059669' : '#D97706',
              }}
            />
          )}
        </Stack>
      )}

      {/* Answer input / display */}
      {item.type === 'text' && (
        readOnly ? (
          <Typography sx={{ fontSize: 13.5, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>{value || '—'}</Typography>
        ) : (
          <TextField value={value} onChange={(e) => { setValue(e.target.value); save({ value_text: e.target.value }) }}
            size="small" fullWidth placeholder="Enter answer..." sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
        )
      )}
      {item.type === 'number' && (
        readOnly ? (
          <Typography sx={{ fontSize: 13.5, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>{numValue || '—'}</Typography>
        ) : (
          <TextField value={numValue} onChange={(e) => { setNumValue(e.target.value); save({ value_number: parseFloat(e.target.value) || 0 }) }}
            size="small" type="number" placeholder="Enter number..." sx={{ width: 200, '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
        )
      )}
      {item.type === 'yes_no' && (
        <Stack direction="row" spacing={1}>
          {['yes', 'no'].map((v) => (
            <Button key={v} size="small" variant={value === v ? 'contained' : 'outlined'} disabled={readOnly}
              onClick={() => { setValue(v); save({ value_text: v }) }}
              sx={{ fontSize: 12.5, px: 3, textTransform: 'capitalize' }}>{v}</Button>
          ))}
          {/* Fixed third opt-out button — always excluded from scoring
              (see checklist-responses.service.js's _scoreResponseItem),
              not a candidate for "correct answer" like Yes/No are. */}
          <Button size="small" variant={value === 'na' ? 'contained' : 'outlined'} color="inherit" disabled={readOnly}
            onClick={() => { setValue('na'); save({ value_text: 'na' }) }}
            sx={{ fontSize: 12.5, px: 3, color: 'text.secondary', borderColor: 'divider' }}>N/A</Button>
        </Stack>
      )}
      {item.type === 'pass_fail' && (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant={value === 'pass' ? 'contained' : 'outlined'} color="success" disabled={readOnly}
            onClick={() => { setValue('pass'); save({ value_text: 'pass' }) }} sx={{ fontSize: 12.5, px: 3 }}>Pass</Button>
          <Button size="small" variant={value === 'fail' ? 'contained' : 'outlined'} color="error" disabled={readOnly}
            onClick={() => { setValue('fail'); save({ value_text: 'fail' }) }} sx={{ fontSize: 12.5, px: 3 }}>Fail</Button>
        </Stack>
      )}
      {item.type === 'condition' && (
        <Stack direction="row" spacing={1}>
          {CONDITION_OPTIONS.map((opt) => (
            <Button key={opt} size="small" variant={value === opt ? 'contained' : 'outlined'} disabled={readOnly}
              onClick={() => { setValue(opt); save({ value_text: opt }) }}
              sx={value === opt
                ? { fontSize: 12.5, px: 3, bgcolor: CONDITION_COLORS[opt], borderColor: CONDITION_COLORS[opt], '&:hover': { bgcolor: CONDITION_COLORS[opt] } }
                : { fontSize: 12.5, px: 3, color: CONDITION_COLORS[opt], borderColor: CONDITION_COLORS[opt] }}>{opt}</Button>
          ))}
        </Stack>
      )}
      {item.type === 'working' && (
        <Stack direction="row" spacing={1}>
          {WORKING_OPTIONS.map((o) => (
            <Button key={o.value} size="small" variant={value === o.value ? 'contained' : 'outlined'} disabled={readOnly}
              onClick={() => { setValue(o.value); save({ value_text: o.value }) }}
              sx={o.color
                ? (value === o.value
                  ? { fontSize: 12.5, px: 3, bgcolor: o.color, borderColor: o.color, '&:hover': { bgcolor: o.color } }
                  : { fontSize: 12.5, px: 3, color: o.color, borderColor: o.color })
                : { fontSize: 12.5, px: 3, color: 'text.secondary', borderColor: 'divider' }}>{o.label}</Button>
          ))}
        </Stack>
      )}
      {item.type === 'multiple_choice' && (
        <Stack spacing={0.75}>
          {(item.options ?? []).map((opt: string) => (
            <Button key={opt} size="small" variant={value === opt ? 'contained' : 'outlined'} disabled={readOnly}
              onClick={() => { setValue(opt); save({ value_text: opt }) }}
              sx={{ fontSize: 12.5, justifyContent: 'flex-start', textTransform: 'none' }}>{opt}</Button>
          ))}
        </Stack>
      )}
      {item.type === 'checkbox' && (
        <Stack spacing={0.75}>
          {(item.options ?? []).map((opt: string) => {
            const selected = (currentAnswer?.value_options ?? []).includes(opt)
            return (
              <Button key={opt} size="small" variant={selected ? 'contained' : 'outlined'} disabled={readOnly}
                onClick={() => {
                  const current = currentAnswer?.value_options ?? []
                  const next = selected ? current.filter((v: string) => v !== opt) : [...current, opt]
                  save({ value_options: next })
                }}
                sx={{ fontSize: 12.5, justifyContent: 'flex-start', textTransform: 'none' }}>{opt}</Button>
            )
          })}
        </Stack>
      )}
      {item.type === 'date' && (
        readOnly ? (
          <Typography sx={{ fontSize: 13.5, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>{value || '—'}</Typography>
        ) : (
          <TextField value={value} onChange={(e) => { setValue(e.target.value); save({ value_text: e.target.value }) }}
            size="small" type="date" sx={{ width: 200, '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} InputLabelProps={{ shrink: true }} />
        )
      )}
      {item.type === 'rating' && (
        <Rating value={numValue ? Number(numValue) : 0} readOnly={readOnly} size="large"
          onChange={(_, v) => { setNumValue(String(v ?? 0)); save({ value_number: v ?? 0 }) }} />
      )}

      {/* Note + attachments — unified card */}
      {readOnly ? (
        (notes || mediaUrls.length > 0) && (
          <Box sx={{ mt: 1.5, p: 2, borderRadius: 2, bgcolor: (t) => alpha(t.palette.warning.main, 0.04),
            border: '1px solid', borderColor: (t) => alpha(t.palette.warning.main, 0.15) }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>Note</Typography>
            {notes && <Typography sx={{ fontSize: 13, lineHeight: 1.55, color: 'text.secondary', mb: mediaUrls.length > 0 ? 1.5 : 0 }}>{notes}</Typography>}
            {mediaUrls.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {mediaUrls.map((m, idx) => (
                  <Tooltip key={idx} title={m.name} arrow>
                    <Box onClick={() => setViewIdx(idx)}
                      sx={{ width: 56, height: 56, borderRadius: 1.5, overflow: 'hidden', cursor: 'pointer',
                        border: '1px solid', borderColor: 'divider', position: 'relative',
                        '&:hover .overlay': { opacity: 1 } }}>
                      {m.type?.startsWith('image/') ? (
                        <Box component="img" src={m.url} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover' }}>
                          <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>{m.name?.split('.').pop()}</Typography>
                        </Box>
                      )}
                      <Box className="overlay" sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 150ms' }}>
                        <VisibilityOutlinedIcon sx={{ fontSize: 16, color: '#fff' }} />
                      </Box>
                    </Box>
                  </Tooltip>
                ))}
              </Stack>
            )}
          </Box>
        )
      ) : (
        <>
          {(showNotes || mediaUrls.length > 0) ? (
            <Box sx={{ mt: 1.5, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
              bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Note</Typography>
                <Stack direction="row" spacing={0.5}>
                  {!readOnly && (
                    <VoiceRecorder
                      onRecorded={async (blob) => {
                        setUploading(true)
                        try {
                          const file = new File([blob], `voice-note-${Date.now()}.webm`, { type: 'audio/webm' })
                          await api.checklists.uploadItemAttachment(workOrderId, responseId, item.id, file)
                          onAnswered()
                          toast.success('Voice note attached')
                        } catch { toast.error('Upload failed') }
                        finally { setUploading(false) }
                      }}
                    />
                  )}
                  <Button size="small" startIcon={uploading ? <CircularProgress size={10} /> : <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 13 }} />}
                    disabled={uploading || readOnly} onClick={() => fileInputRef.current?.click()}
                    sx={{ fontSize: 10.5, fontWeight: 600, color: 'text.secondary', textTransform: 'none', minWidth: 0, px: 1 }}>
                    {uploading ? 'Uploading...' : 'Attach'}
                  </Button>
                </Stack>
              </Stack>

              <TextField value={notes} onChange={(e) => saveNotes(e.target.value)} size="small" fullWidth
                placeholder="Describe the issue, observation, or reason..."
                multiline minRows={2} sx={{ '& .MuiOutlinedInput-root': { fontSize: 12.5, bgcolor: 'background.paper' } }} />

              {mediaUrls.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                  {mediaUrls.map((m, idx) => (
                    <MediaCard key={idx} media={m} idx={idx} readOnly={readOnly}
                      workOrderId={workOrderId} responseId={responseId} itemId={item.id}
                      onView={() => setViewIdx(idx)}
                      onRemove={async () => {
                        try { await api.checklists.deleteItemAttachment(workOrderId, responseId, item.id, idx); onAnswered(); toast.success('Removed') }
                        catch { toast.error('Failed to remove') }
                      }}
                      onUpdate={onAnswered} />
                  ))}
                </Stack>
              )}
            </Box>
          ) : (
            <Box sx={{ mt: 1.5 }}>
              <Button size="small" startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 13 }} />}
                onClick={() => setShowNotes(true)}
                sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary', textTransform: 'none' }}>
                Add note
              </Button>
            </Box>
          )}
          <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx" style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files?.[0]
              if (!file) return
              e.target.value = ''
              if (!showNotes) setShowNotes(true)
              // Image picks go through the markup dialog first; the
              // user can draw on them or skip and upload as-is. Non-
              // image files (pdf, doc) take the direct path — no
              // sense painting on a Word doc.
              if (file.type.startsWith('image/')) {
                setAnnotatorFile(file)
                return
              }
              setUploading(true)
              try {
                await api.checklists.uploadItemAttachment(workOrderId, responseId, item.id, file)
                onAnswered()
                toast.success('Attached')
              } catch { toast.error('Upload failed') }
              finally { setUploading(false) }
            }} />

          <ImageAnnotator
            open={annotatorFile !== null}
            file={annotatorFile}
            onCancel={() => setAnnotatorFile(null)}
            onDone={async (annotated) => {
              setAnnotatorFile(null)
              setUploading(true)
              try {
                await api.checklists.uploadItemAttachment(workOrderId, responseId, item.id, annotated)
                onAnswered()
                toast.success('Attached')
              } catch { toast.error('Upload failed') }
              finally { setUploading(false) }
            }}
          />
        </>
      )}

      {/* Document viewer for media */}
      {viewIdx !== null && mediaUrls[viewIdx] && (
        <DocumentViewer
          open
          doc={{ id: String(viewIdx), file_url: mediaUrls[viewIdx].url, file_name: mediaUrls[viewIdx].name, file_size: mediaUrls[viewIdx].size, content_type: mediaUrls[viewIdx].type, created_at: '', uploaded_by_name: null }}
          fetchBlob={() => api.checklists.viewItemAttachment(workOrderId, responseId, item.id, viewIdx)}
          onClose={() => setViewIdx(null)}
        />
      )}
    </Box>
  )
}

const TAG_OPTIONS = ['Before repair', 'After repair', 'Damage', 'Safety hazard', 'Evidence', 'Reference', 'Other']

function MediaCard({ media, idx, readOnly, workOrderId, responseId, itemId, onView, onRemove, onUpdate }: {
  media: any; idx: number; readOnly: boolean; workOrderId: string; responseId: string; itemId: string
  onView: () => void; onRemove: () => void; onUpdate: () => void
}) {
  const [caption, setCaption] = useState(media.caption ?? '')
  const [tag, setTag] = useState(media.tag ?? '')
  const [expanded, setExpanded] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  function saveMetadata(updates: { caption?: string; tag?: string }) {
    if (readOnly) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        await api.checklists.updateItemAttachment(workOrderId, responseId, itemId, idx, updates)
        onUpdate()
      } catch {}
    }, 600)
  }

  return (
    <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden',
      transition: 'border-color 120ms', '&:hover': { borderColor: 'primary.main' } }}>
      {/* Thumbnail / audio row */}
      <Stack direction="row" spacing={1.5} sx={{ p: 1.25 }}>
        {media.type?.startsWith('audio/') ? (
          <Box sx={{ width: 40, height: 40, borderRadius: 99, bgcolor: '#7C3AED14', color: '#7C3AED',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <MicOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
        ) : (
          <Box onClick={onView}
            sx={{ width: 52, height: 52, borderRadius: 1.5, overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
              border: '1px solid', borderColor: 'divider', position: 'relative', '&:hover .view-ol': { opacity: 1 } }}>
            {media.type?.startsWith('image/') ? (
              <Box component="img" src={media.url} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover' }}>
                <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>{media.name?.split('.').pop()}</Typography>
              </Box>
            )}
            <Box className="view-ol" sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 150ms' }}>
              <VisibilityOutlinedIcon sx={{ fontSize: 14, color: '#fff' }} />
            </Box>
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {media.type?.startsWith('audio/') ? (
            <Box>
              <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#7C3AED', mb: 0.5 }}>Voice note</Typography>
              <Box component="audio" controls src={media.url} sx={{ width: '100%', height: 28, '&::-webkit-media-controls-panel': { bgcolor: 'transparent' } }} />
            </Box>
          ) : (
            <Typography sx={{ fontSize: 12, fontWeight: 500 }} noWrap>{media.name}</Typography>
          )}
          {media.tag && <Chip label={media.tag} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, mt: 0.5, bgcolor: '#3B82F614', color: '#3B82F6' }} />}
          {media.caption && <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }} noWrap>{media.caption}</Typography>}
          {!media.tag && !media.caption && !readOnly && (
            <Typography onClick={() => setExpanded(true)}
              sx={{ fontSize: 11, color: 'primary.main', cursor: 'pointer', mt: 0.25, fontWeight: 500 }}>
              Add description
            </Typography>
          )}
        </Box>
        <Stack spacing={0.25} alignItems="center">
          {!readOnly && (
            <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ color: 'text.disabled', p: 0.25 }}>
              {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          )}
          {!readOnly && (
            <IconButton size="small" onClick={onRemove} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' }, p: 0.25 }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {/* Expanded edit section */}
      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, pb: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={1} sx={{ mt: 1.25 }}>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {TAG_OPTIONS.map((t) => (
                <Chip key={t} label={t} size="small" onClick={() => { setTag(t); saveMetadata({ tag: t, caption }) }}
                  sx={{ height: 24, fontSize: 10.5, fontWeight: 600, cursor: 'pointer',
                    bgcolor: tag === t ? '#3B82F614' : 'action.hover',
                    color: tag === t ? '#3B82F6' : 'text.secondary',
                    border: '1px solid', borderColor: tag === t ? '#3B82F6' : 'transparent' }} />
              ))}
            </Stack>
            <TextField value={caption} onChange={(e) => { setCaption(e.target.value); saveMetadata({ caption: e.target.value, tag }) }}
              size="small" fullWidth placeholder="Describe this image..."
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 12.5 } }} />
          </Stack>
        </Box>
      </Collapse>
    </Box>
  )
}

function VoiceRecorder({ onRecorded }: { onRecorded: (blob: Blob) => void }) {
  const [recording, setRecording] = useState(false)
  const [duration, setDuration] = useState(0)
  const mediaRecRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' })
      chunksRef.current = []
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop())
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        if (blob.size > 0) onRecorded(blob)
      }
      recorder.start()
      mediaRecRef.current = recorder
      setRecording(true)
      setDuration(0)
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000)
    } catch {
      toast.error('Microphone access denied')
    }
  }

  function stopRecording() {
    if (mediaRecRef.current?.state === 'recording') {
      mediaRecRef.current.stop()
    }
    if (timerRef.current) clearInterval(timerRef.current)
    setRecording(false)
    setDuration(0)
  }

  const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

  if (recording) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.75}
        sx={{ px: 1.25, py: 0.5, borderRadius: 99, bgcolor: '#DC262614', border: '1px solid', borderColor: '#DC262640' }}>
        <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: '#DC2626', animation: 'pulse 1s infinite',
          '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#DC2626', fontVariantNumeric: 'tabular-nums', minWidth: 32 }}>
          {fmtTime(duration)}
        </Typography>
        <IconButton size="small" onClick={stopRecording} sx={{ color: '#DC2626', p: 0.25 }}>
          <StopCircleOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>
    )
  }

  return (
    <Tooltip title="Record voice note" arrow>
      <IconButton size="small" onClick={startRecording}
        sx={{ color: 'text.secondary', '&:hover': { color: '#7C3AED' }, p: 0.5 }}>
        <MicOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  )
}

/// Modal that lets the tech spin up a follow-up Work Order or Request
/// from an in-progress checklist item without leaving the execution
/// flow. The new record inherits the parent WO's site / location /
/// asset by default; the user just types a title and (optionally)
/// description + priority.
function _ReportFromItemDialog({
  workOrderId, responseId, itemId, itemLabel, onClose, onCreated,
}: {
  workOrderId: string; responseId: string; itemId: string
  itemLabel: string
  onClose: () => void
  onCreated: () => void
}) {
  const [kind, setKind] = useState<'work_order' | 'request'>('work_order')
  // Title defaults to "Follow-up: <item label>" so the link in the WO
  // list immediately reads as a checklist escalation. The user can
  // edit before submitting.
  const [title, setTitle] = useState(`Follow-up: ${itemLabel}`)
  const [description, setDescription] = useState(`Reported from checklist item: ${itemLabel}`)
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'critical'>('medium')

  const createM = useMutation({
    mutationFn: () => api.checklists.linkItemFollowUp(workOrderId, responseId, itemId, {
      kind,
      body: { title: title.trim(), description: description.trim() || undefined, priority },
    }),
    onSuccess: (res) => {
      toast.success(
        res.kind === 'work_order'
          ? `Created ${res.wo_code ?? 'work order'}`
          : 'Request submitted',
      )
      onCreated()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to create'),
  })

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 1.5 }}>
        Report an issue
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25, fontWeight: 500 }}>
          From: {itemLabel}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.06em', color: 'text.secondary', mb: 0.75 }}>
              Create
            </Typography>
            <ToggleButtonGroup
              value={kind} exclusive size="small"
              onChange={(_, v) => v && setKind(v)}
              sx={{ '& .MuiToggleButton-root': { fontSize: 12, px: 1.75, py: 0.5 } }}>
              <ToggleButton value="work_order">
                <BuildOutlinedIcon sx={{ fontSize: 14, mr: 0.5 }} />Work order
              </ToggleButton>
              <ToggleButton value="request">
                <AssignmentOutlinedIcon sx={{ fontSize: 14, mr: 0.5 }} />Request
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.75 }}>
              {kind === 'work_order'
                ? 'A new work order is created in the open queue, inheriting the current site / location / asset.'
                : 'A new request is submitted for review. It becomes a work order when approved.'}
            </Typography>
          </Box>

          <TextField required label="Title" value={title} onChange={(e) => setTitle(e.target.value)}
            size="small" fullWidth autoFocus />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)}
            size="small" fullWidth multiline minRows={2} />

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.06em', color: 'text.secondary', mb: 0.5 }}>
              Priority
            </Typography>
            <Select value={priority} onChange={(e) => setPriority(e.target.value as any)}
              size="small" fullWidth>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={createM.isPending}
          sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained"
          onClick={() => createM.mutate()}
          disabled={createM.isPending || !title.trim()}
          sx={{ fontSize: 13, fontWeight: 600 }}>
          {createM.isPending
            ? 'Creating…'
            : (kind === 'work_order' ? 'Create work order' : 'Submit request')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
